import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Button,
    Space,
    Input,
    Select,
    Checkbox,
    message,
    Typography,
    Popconfirm,
    Empty
} from 'antd';
import {
    PlusOutlined,
    SaveOutlined,
    DeleteOutlined,
    HolderOutlined,
    PlusCircleOutlined
} from '@ant-design/icons';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { pbxService } from '../services/api';

const { Text, Title } = Typography;
const { Option } = Select;

// Sortable Field Item Component
const SortableFieldItem = ({ field, index, onFieldChange, onRemove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id || `virtual-${index}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: 16,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 16,
                backgroundColor: '#fff',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12
            }}>
                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    style={{
                        cursor: 'grab',
                        padding: '8px 4px',
                        color: '#1890ff',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <HolderOutlined style={{ fontSize: 18 }} />
                </div>

                {/* Field Configuration */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Etiqueta</Text>
                            <Input
                                value={field.label}
                                onChange={(e) => onFieldChange(index, 'label', e.target.value)}
                                placeholder="Ej. Edad"
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Clave (Key)</Text>
                            <Input
                                value={field.field_key}
                                onChange={(e) => onFieldChange(index, 'field_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                placeholder="ej_edad"
                                disabled={!!field.id}
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Tipo</Text>
                            <Select
                                value={field.field_type}
                                style={{ width: '100%' }}
                                onChange={(val) => onFieldChange(index, 'field_type', val)}
                            >
                                <Option value="text">Texto</Option>
                                <Option value="number">Número</Option>
                                <Option value="enum">Lista</Option>
                            </Select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <Checkbox
                            checked={field.is_required === 1 || field.is_required === true}
                            onChange={(e) => onFieldChange(index, 'is_required', e.target.checked ? 1 : 0)}
                        >
                            Obligatorio
                        </Checkbox>
                        {field.field_type === 'enum' && (
                            <div style={{ flex: 1 }}>
                                <Input
                                    placeholder="Opción 1, Opción 2..."
                                    value={Array.isArray(field.options) ? field.options.join(', ') : field.options}
                                    onChange={(e) => {
                                        onFieldChange(index, 'options', e.target.value);
                                    }}
                                />
                                <Text type="secondary" style={{ fontSize: 11 }}>Separa por comas</Text>
                            </div>
                        )}
                    </div>
                </div>

                {/* Delete Button */}
                <Popconfirm
                    title="¿Remover este campo?"
                    onConfirm={() => onRemove(index)}
                    okText="Sí"
                    cancelText="No"
                >
                    <Button icon={<DeleteOutlined />} danger size="small" />
                </Popconfirm>
            </div>
        </div>
    );
};

const PBXFieldsManager = ({ visible, onClose, formInfo }) => {
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (visible && formInfo) {
            fetchFields();
        }
    }, [visible, formInfo]);

    const fetchFields = async () => {
        setLoading(true);
        try {
            const res = await pbxService.getForm(formInfo.id);
            if (res.status === 'success') {
                setFields(res.data.fields || []);
                setHasChanges(false);
            }
        } catch (error) {
            message.error('Error al cargar campos');
        } finally {
            setLoading(false);
        }
    };

    const handleAddField = () => {
        const newField = {
            id: null,
            label: 'Nuevo Campo',
            field_key: 'campo_' + Math.random().toString(36).substring(7),
            field_type: 'text',
            options: [],
            is_required: 0,
            sort_order: fields.length + 1
        };
        setFields([...fields, newField]);
        setHasChanges(true);
    };

    const handleFieldChange = (index, key, value) => {
        const newFields = [...fields];
        newFields[index][key] = value;
        setFields(newFields);
        setHasChanges(true);
    };

    const handleRemoveField = (index) => {
        const newFields = [...fields];
        newFields.splice(index, 1);
        setFields(newFields);
        setHasChanges(true);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setFields((items) => {
                const oldIndex = items.findIndex((item) => (item.id || `virtual-${items.indexOf(item)}`) === active.id);
                const newIndex = items.findIndex((item) => (item.id || `virtual-${items.indexOf(item)}`) === over.id);

                const reordered = arrayMove(items, oldIndex, newIndex);
                setHasChanges(true);
                return reordered;
            });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Re-calc sort order (starting at 1)
            const fieldsToSave = fields.map((f, i) => {
                let processedOptions = f.options;
                if (typeof f.options === 'string') {
                    processedOptions = f.options.split(',').map(s => s.trim()).filter(s => s !== '');
                } else if (Array.isArray(f.options)) {
                    processedOptions = f.options.map(s => (typeof s === 'string' ? s.trim() : s)).filter(s => s !== '');
                }

                return {
                    ...f,
                    options: processedOptions,
                    sort_order: i + 1
                };
            });

            const res = await pbxService.syncFields(formInfo.id, fieldsToSave);
            if (res.status === 'success') {
                message.success('Campos sincronizados correctamente');
                setHasChanges(false);
                fetchFields();
            }
        } catch (error) {
            message.error('Error al sincronizar campos');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={
                <div>
                    <Text strong>Campos del Formulario: </Text>
                    <Text type="danger">{formInfo.name}</Text>
                </div>
            }
            width={900}
            onClose={onClose}
            open={visible}
            maskClosable={!hasChanges}
            extra={
                <Space>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={handleAddField}
                    >
                        Añadir Campo
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={loading}
                        disabled={!hasChanges}
                    >
                        Sincronizar Cambios
                    </Button>
                </Space>
            }
        >
            {fields.length === 0 ? (
                <Empty
                    description="No hay campos definidos"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Button type="dashed" icon={<PlusCircleOutlined />} onClick={handleAddField}>
                        Comenzar a añadir campos
                    </Button>
                </Empty>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={fields.map((field, idx) => field.id || `virtual-${idx}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map((field, index) => (
                            <SortableFieldItem
                                key={field.id || `virtual-${index}`}
                                field={field}
                                index={index}
                                onFieldChange={handleFieldChange}
                                onRemove={handleRemoveField}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}

            {hasChanges && (
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Text type="warning">Tienes cambios sin sincronizar.</Text>
                </div>
            )}
        </Drawer>
    );
};

export default PBXFieldsManager;
